"use client";

import React, { useEffect } from "react";
import { CHATBOT_ALLOW_ATTR } from "./chatbotGuardSnippet";

declare global {
  interface Window {
    Tawk_API?: Record<string, unknown> & {
      onChatStarted?: () => void;
    };
    Tawk_LoadStart?: Date;
    gtag?: (...args: unknown[]) => void;
  }
}

type TawkProps = {
  propertyId?: string | null;
  widgetId?: string | null;
  sendTo?: string | null;
};

const Tawk = ({ propertyId: propertyIdProp, widgetId: widgetIdProp, sendTo: sendToProp }: TawkProps = {}) => {
  const propertyId =
    (propertyIdProp && propertyIdProp.trim()) || process.env.NEXT_PUBLIC_TAWK_PROPERTY_ID;
  const widgetId =
    (widgetIdProp && widgetIdProp.trim()) ||
    process.env.NEXT_PUBLIC_TAWK_WIDGET_ID ||
    "default";
  const sendTo = (sendToProp && sendToProp.trim()) || process.env.NEXT_PUBLIC_GOOGLE_ADS_CODE;

  useEffect(() => {
    // Only rendered when the CMS selected Tawk, so a missing property id is a broken deploy,
    // not a normal state — it went unnoticed on CU prod because it failed silently.
    if (!propertyId) {
      console.warn(
        "[Tawk] chatbot_provider is 'tawk' but NEXT_PUBLIC_TAWK_PROPERTY_ID is missing from this build — no widget will load."
      );
      return;
    }

    // Same as Tawk snippet: object exists before the embed runs so the loader can attach to it.
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    let cancelled = false;
    let injectedScript: HTMLScriptElement | null = null;

    console.log("Attaching chat started");
    
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_API.onChatStarted = () => {
      console.log("Chat started");
      
      if (sendTo && typeof window.gtag === "function") {
        console.log("Sending conversion to Google Ads", sendTo);
        window.gtag("event", "conversion", { send_to: sendTo });
      }
      console.log("Pushing to data layer");

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "tawk_chat_started" });
    };

    const inject = () => {
      if (cancelled) return;
      console.log("Injecting script");
      const s1 = document.createElement("script");
      s1.async = true;
      s1.src = `https://embed.tawk.to/${propertyId}/${widgetId}`;
      s1.charset = "UTF-8";
      s1.setAttribute("crossorigin", "*");
      // This injection is provider-gated, so opt it out of the ChatbotGuard block.
      s1.setAttribute(CHATBOT_ALLOW_ATTR, "");

      injectedScript = s1;

      const s0 = document.getElementsByTagName("script")[0];
      if (s0?.parentNode) {
        console.log("Inserting script before head");
        s0.parentNode.insertBefore(s1, s0);
      } else {
        console.log("Appending script to head");
        document.head.appendChild(s1);
      }
    };

    const useIdleCallback = typeof window.requestIdleCallback === "function";
    let idleId: number;
    if (useIdleCallback) {
      idleId = window.requestIdleCallback(() => inject(), {
        timeout: 4000,
      }) as unknown as number;
    } else {
      idleId = window.setTimeout(inject, 1) as unknown as number;
    }

    return () => {
      cancelled = true;
      if (useIdleCallback) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
      if (injectedScript) {
        injectedScript = null;
      }
      if (window.Tawk_API) {
        delete window.Tawk_API.onChatStarted;
      }
    };
  }, []);

  return null;
};

export default Tawk;
